import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut } from "lucide-react";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { toast } from "sonner";

import StatisticsUser from "./user/1-1.StatisticsUser";
import CurationResultAnalysisPage from "./user/CurationResultAnalysisPage";
import SnackRequestPage from "./user/SnackRequestPage";
import AICurationPage from "./admin/aicuration/AICurationPage";
import { Button } from "../components/ui/button";
import { Spinner } from "../components/ui/spinner";

type UserMenuKey =
  | "statistics"
  | "ai-curation"
  | "curation-result-analysis"
  | "request";

const UserDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userName, setUserName] = React.useState("");
  const [affiliation, setAffiliation] = React.useState("");
  const [isDescribeUser, setIsDescribeUser] = React.useState(false);
  const [activeMenu, setActiveMenu] = React.useState<UserMenuKey>("statistics");
  const [loadingProfile, setLoadingProfile] = React.useState(true);

  React.useEffect(() => {
    const auth = getAuth();
    const db = getFirestore();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoadingProfile(false);
        if (!location.pathname.startsWith("/register")) navigate("/login");
        return;
      }

      if (location.pathname.startsWith("/register")) return;

      try {
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (!userDocSnap.exists()) {
          console.warn("Firestore에 사용자 정보가 없습니다.");
          setLoadingProfile(false);
          return;
        }

        const data = userDocSnap.data();
        const snackSubscriptionUser = Boolean(data.isDescribe);

        setUserName(data.name || "");
        setAffiliation(data.affiliation || "");
        setIsDescribeUser(snackSubscriptionUser);
        setActiveMenu(snackSubscriptionUser ? "ai-curation" : "statistics");
      } catch (profileError) {
        console.error(profileError);
        toast.error("사용자 정보를 불러오지 못했습니다.");
      } finally {
        setLoadingProfile(false);
      }
    });

    return () => unsubscribe();
  }, [location.pathname, navigate]);

  const handleLogout = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      toast.success("로그아웃 되었습니다.");
      navigate("/login");
    } catch {
      toast.error("로그아웃에 실패했습니다.");
    }
  };

  const navItems: Array<{ key: UserMenuKey; label: string }> = isDescribeUser
    ? [
        { key: "ai-curation", label: "AI 큐레이션" },
        { key: "curation-result-analysis", label: "큐레이션 결과/분석" },
        { key: "request", label: "요청하기" },
      ]
    : [{ key: "statistics", label: "이용 통계" }];

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-black bg-black text-white">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-4 px-6 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
            <a
              href="https://buddie.kr/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center"
            >
              <img
                src="https://buddie.kr/images/img_footer_logo.png"
                alt="Logo"
                style={{ height: 40 }}
              />
            </a>

            <nav className="flex flex-wrap items-center gap-2">
              {navItems.map((item) => {
                const active = activeMenu === item.key;
                const hideBorder = item.key !== "statistics";
                return (
                  <Button
                    key={item.key}
                    variant={active ? "default" : "outline"}
                    className={`h-10 rounded-full px-4 font-bold ${
                      active
                        ? "bg-transparent text-white hover:bg-white/10"
                        : `${hideBorder ? "border-0" : "border-white/15"} bg-transparent text-white hover:bg-white/10 hover:text-white`
                    }`}
                    onClick={() => setActiveMenu(item.key)}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </nav>
          </div>

          <div className="flex flex-wrap items-center gap-3 lg:justify-end">
            <div className="text-sm font-bold text-white/90">
              {affiliation && userName && `${affiliation} ${userName} 님 환영합니다.`}
            </div>
            <Button
              variant="outline"
              className="h-10 border-white/15 bg-transparent text-white hover:bg-white/10 hover:text-white"
              onClick={handleLogout}
              title="로그아웃"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1440px] p-6 lg:p-10">
        {loadingProfile ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center gap-3">
            <Spinner size="lg" />
            <span className="text-sm text-slate-500">사용자 정보를 불러오는 중...</span>
          </div>
        ) : activeMenu === "ai-curation" ? (
          <AICurationPage
            lockedAffiliation={affiliation}
            customerFacing
          />
        ) : activeMenu === "curation-result-analysis" ? (
          <CurationResultAnalysisPage affiliation={affiliation} />
        ) : activeMenu === "request" ? (
          <SnackRequestPage />
        ) : (
          <StatisticsUser />
        )}
      </main>
    </div>
  );
};

export default UserDashboard;
